import { useCallback, useMemo } from 'react'
import {
  BacktestStatusPill,
  ChartConfigBar,
  ChartPane,
  ChartReplayBar,
} from '@/components'
import type { BacktestStatus, TickReplayState } from '@/components'
import BacktestChart, { type BacktestChartHandle } from './BacktestChart'
import type { CandleData, OrderData } from '../types/chartData'
import BacktestConfigGroups from './BacktestConfigGroups'
import type { BacktestResult } from '../api'
import { AVAILABLE_SPEEDS } from '../hooks/useBacktestChartState'
import useScrubProjection from '../hooks/useScrubProjection'
import { TIMEFRAME_OPTIONS, TIMEFRAME_LABELS } from '@/components/Workbench/catalog'

// The same set the Create tab offers. The old M1/H1/D1 list was a limitation of
// the per-day HTTP candle endpoint; the WebSocket datafeed serves every
// timeframe. A backtest configured on something outside this set (M2, H3, …)
// gets its own timeframe appended so the user can always return to it.
const STANDARD_TIMEFRAMES: readonly string[] = TIMEFRAME_OPTIONS

interface BotBacktestTopSectionProps {
  symbol: string
  timeframe: string
  startDate: string
  endDate: string
  speed: number
  isPlaying: boolean
  isPlayDisabled: boolean
  candles: CandleData[]
  orders: OrderData[]
  maxAvailableTs: number | null
  chartRef: React.RefObject<BacktestChartHandle>
  configInput: BacktestResult['configInput']
  onVisibleRangeChange: (range: { from: number; to: number }) => void
  onUserNavigate: () => void
  onReset: () => void
  onPlay: () => void
  onPause: () => void
  onTimeframeChange: (tf: string) => void
  onSpeedChange: (speed: number) => void
  onCycleSpeed: () => void
  onScrubTime: (timestamp: number) => void
  isInProgress: boolean
  isBacktestCompleted?: boolean
  progressPercent: number
  progressViewPosition: number
  rangeStartTs: number
  rangeEndTs: number
  backtestStartedAtMs: number | null
  playStartedAtMs: number | null
  playElapsedSec: number
  progressViewTs: number
  tickReplay?: TickReplayState
}

export default function BotBacktestTopSection({
  symbol,
  timeframe,
  startDate,
  endDate,
  speed,
  isPlaying,
  isPlayDisabled,
  candles,
  orders,
  maxAvailableTs,
  chartRef,
  configInput,
  onVisibleRangeChange,
  onUserNavigate,
  onReset,
  onPlay,
  onPause,
  onTimeframeChange,
  onSpeedChange,
  onCycleSpeed,
  onScrubTime,
  isInProgress,
  isBacktestCompleted = true,
  progressPercent,
  progressViewPosition,
  rangeStartTs,
  rangeEndTs,
  backtestStartedAtMs,
  playStartedAtMs,
  playElapsedSec,
  progressViewTs,
  tickReplay,
}: BotBacktestTopSectionProps) {
  const {
    scrubOhlc,
    visibleCount: replayVisibleCount,
    totalCandles,
    maxScrubIndex,
    handleScrub,
  } = useScrubProjection({
    rangeStartTs,
    rangeEndTs,
    maxAvailableTs,
    progressViewPosition,
    onScrubTime,
  })

  const handleTogglePlay = useCallback(() => {
    if (isPlaying) {
      onPause()
      return
    }
    if (isPlayDisabled) return
    onPlay()
  }, [isPlaying, isPlayDisabled, onPlay, onPause])

  const pill = useMemo<{
    status: BacktestStatus
    startedAt: number | null
    elapsed: number
  }>(() => {
    if (isInProgress) {
      // "starting" = backtest is running but no candles have been produced yet.
      // Once maxAvailableTs advances past rangeStartTs, switch to live "running" with elapsed
      // since backtest.created.
      const hasAnyCandles = maxAvailableTs !== null && maxAvailableTs > rangeStartTs
      if (!hasAnyCandles) {
        return { status: 'starting', startedAt: null, elapsed: 0 }
      }
      return { status: 'running', startedAt: backtestStartedAtMs ?? null, elapsed: 0 }
    }

    if (isPlaying) {
      return { status: 'replaying', startedAt: playStartedAtMs, elapsed: playElapsedSec }
    }

    if (progressViewTs >= rangeEndTs) {
      return { status: 'complete', startedAt: null, elapsed: 0 }
    }

    return { status: 'paused', startedAt: null, elapsed: playElapsedSec }
  }, [
    isInProgress,
    isPlaying,
    maxAvailableTs,
    rangeStartTs,
    rangeEndTs,
    backtestStartedAtMs,
    playStartedAtMs,
    playElapsedSec,
    progressViewTs,
  ])

  const timeframes = useMemo(
    () =>
      STANDARD_TIMEFRAMES.includes(timeframe)
        ? STANDARD_TIMEFRAMES
        : [...STANDARD_TIMEFRAMES, timeframe],
    [timeframe],
  )

  const configBar = (
    <ChartConfigBar
      symbol={symbol}
      timeframe={timeframe}
      onTimeframeChange={onTimeframeChange}
      timeframes={timeframes}
      timeframeLabels={TIMEFRAME_LABELS}
      dateFrom={startDate}
      dateTo={endDate}
      extraConfigSlot={<BacktestConfigGroups configInput={configInput} />}
      statusSlot={
        <BacktestStatusPill
          status={pill.status}
          startedAt={pill.startedAt}
          elapsed={pill.elapsed}
        />
      }
    />
  )

  const chartSlot = (
    <BacktestChart
      ref={chartRef}
      candles={candles}
      orders={orders}
      symbol={symbol}
      timeframe={timeframe}
      autoSetInitialVisibleRange={false}
      boundPanToData
      maxAvailableTimestamp={maxAvailableTs ?? undefined}
      onVisibleRangeChange={onVisibleRangeChange}
      onUserNavigate={onUserNavigate}
    />
  )

  const replayBar = (
    <ChartReplayBar
      ohlc={scrubOhlc}
      playing={isPlaying}
      speed={speed}
      visibleCount={replayVisibleCount}
      totalCandles={totalCandles}
      maxScrubIndex={maxScrubIndex}
      bufferPct={progressPercent}
      backtestRunning={isInProgress}
      onRestart={onReset}
      onTogglePlay={handleTogglePlay}
      onCycleSpeed={onCycleSpeed}
      onSpeedChange={onSpeedChange}
      onScrub={handleScrub}
      speeds={AVAILABLE_SPEEDS}
      tickReplay={tickReplay}
    />
  )

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <ChartPane
        configBarSlot={configBar}
        chartSlot={isBacktestCompleted ? chartSlot : <div className="h-full invisible" aria-hidden="true">{chartSlot}</div>}
        replayBarSlot={isBacktestCompleted ? replayBar : <div className="invisible" aria-hidden="true">{replayBar}</div>}
      />
    </div>
  )
}
